import * as React from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import FastForwardRoundedIcon from "@mui/icons-material/FastForwardRounded";
import { MIN_ELEVATION_DEG, linkLock, type GroundStation, type PassWindow } from "./passWindow";
import { DataField, DataLabel, Led, MCS_COLORS, MONO, Panel, StatusLamp, formatClock, formatDuration } from "./mcsUi";

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const compass = (deg: number) => COMPASS[Math.round(deg / 45) % 8];

function LinkStep(props: { label: string; on: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Led color={props.on ? MCS_COLORS.ok : "rgba(226,232,240,0.18)"} size={7} />
      <Typography sx={{ fontFamily: MONO, fontSize: 11, color: props.on ? "rgba(226,232,240,0.9)" : MCS_COLORS.idle }}>{props.label}</Typography>
    </Stack>
  );
}

// The pass from rise (AOS) to set (LOS) as a bar, with the highest point
// (TCA) and where the pass is now.
function PassTimeline(props: { pw: PassWindow }) {
  const { pass, simNow, inContact } = props.pw;
  if (!pass) return null;
  const aos = pass.aos.getTime();
  const los = pass.los.getTime();
  const at = (t: number) => `${Math.min(100, Math.max(0, ((t - aos) / (los - aos)) * 100))}%`;
  return (
    <Stack spacing={0.6}>
      <Box sx={{ position: "relative", height: 10, borderRadius: 5, bgcolor: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,85,247,0.25)" }}>
        {inContact && (
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: at(simNow.getTime()), borderRadius: 5, bgcolor: "rgba(110,231,183,0.35)" }} />
        )}
        <Box sx={{ position: "absolute", left: at(pass.maxAt.getTime()), top: -3, bottom: -3, width: 2, bgcolor: "#C4B5FD" }} />
        {inContact && (
          <Box sx={{ position: "absolute", left: at(simNow.getTime()), top: -4, width: 3, height: 18, ml: "-1px", bgcolor: MCS_COLORS.ok, borderRadius: 1 }} />
        )}
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: "rgba(226,232,240,0.6)" }}>AOS {formatClock(pass.aos)}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: "#C4B5FD" }}>
          TCA {formatClock(pass.maxAt)} · max {pass.maxElevationDeg.toFixed(0)}°
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: "rgba(226,232,240,0.6)" }}>LOS {formatClock(pass.los)}</Typography>
      </Stack>
    </Stack>
  );
}

// Ground station pass and antenna: when the satellite is in view, where the
// antenna points, and how far the radio link has locked on. All times are
// the simulated mission clock (UTC).
export default function PassPanel(props: { station: GroundStation | null; pw: PassWindow; canSkip: boolean }) {
  const { station, pw, canSkip } = props;
  const link = linkLock(pw);
  const status = !station
    ? { label: "NO STATION", color: MCS_COLORS.alarm }
    : pw.noPass
      ? { label: "NO PASS", color: MCS_COLORS.alarm }
      : link.commandLink
        ? { label: "LINK LOCKED", color: MCS_COLORS.ok }
        : pw.inContact
          ? { label: "ACQUIRING", color: MCS_COLORS.info }
          : { label: "WAITING FOR AOS", color: MCS_COLORS.warn };

  return (
    <Panel title="Ground Station · Pass" right={<StatusLamp color={status.color} label={status.label} pulsing={pw.inContact && !link.commandLink} />}>
      {!station ? (
        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13, lineHeight: 1.6 }}>
          The ground station for your workshop isn't set up yet, so no pass can be predicted and no command can be sent. Please ask
          our team.
        </Typography>
      ) : pw.noPass ? (
        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13, lineHeight: 1.6 }}>
          With your satellite's orbit it never rises above {MIN_ELEVATION_DEG}° over {station.name} in the next three days, so no
          command can reach it. Please ask our team.
        </Typography>
      ) : (
        <Stack spacing={1.75}>
          <Typography sx={{ color: "rgba(226,232,240,0.78)", fontSize: 12.5, lineHeight: 1.55 }}>
            {pw.inContact
              ? `In view of ${station.name}. During a pass the clock runs in real time; send your command before LOS.`
              : `Out of view. The clock fast-forwards to the next pass over ${station.name}; about ${formatDuration(pw.realSecondsToAos)} of real time to wait.`}
          </Typography>

          <PassTimeline pw={pw} />

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.5 }}>
            {pw.inContact && pw.pass ? (
              <DataField
                label="LOS in"
                value={formatDuration((pw.pass.los.getTime() - pw.simNow.getTime()) / 1000)}
                color={MCS_COLORS.ok}
                title="Mission time left in this pass (real time)"
              />
            ) : (
              <DataField
                label="AOS in"
                value={pw.pass ? formatDuration((pw.pass.aos.getTime() - pw.simNow.getTime()) / 1000) : "—"}
                title="Mission time until the satellite rises; fast-forwarded"
              />
            )}
            <DataField
              label="Antenna az / el"
              value={pw.look ? `${pw.look.azimuthDeg.toFixed(1)}° ${compass(pw.look.azimuthDeg)} / ${pw.look.elevationDeg.toFixed(1)}°` : "—"}
              stale={!pw.inContact}
              title="Where the antenna points: compass direction and height above the horizon"
            />
            <DataField
              label="Range"
              value={pw.look ? `${Math.round(pw.look.rangeKm).toLocaleString("en-US")} km` : "—"}
              stale={!pw.inContact}
              title="Distance from the ground station to the satellite"
            />
          </Box>

          <Stack spacing={0.75}>
            <DataLabel>Link chain</DataLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
              <LinkStep label="Carrier" on={link.carrier} />
              <LinkStep label="Bit sync" on={link.bitSync} />
              <LinkStep label="Frame sync" on={link.frameSync} />
              <LinkStep label="TC link" on={link.commandLink} />
            </Box>
          </Stack>

          {pw.inContact && canSkip && (
            <Button
              size="small"
              startIcon={<FastForwardRoundedIcon />}
              onClick={pw.skipToNextPass}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD" }}
            >
              End this pass and fast-forward to the next one
            </Button>
          )}
        </Stack>
      )}
    </Panel>
  );
}
